import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Loader2, ChevronDown, ChevronRight, Receipt,
  CreditCard, Banknote, Smartphone, RefreshCw,
  Search, X, Calendar,
} from "lucide-react";
import api from "../../lib/api";

const PAYMENT_ICONS = {
  card:  <CreditCard  className="w-3 h-3" />,
  cash:  <Banknote    className="w-3 h-3" />,
  bizum: <Smartphone  className="w-3 h-3" />,
};

const SOURCE_STYLES = {
  webhook:      "bg-green-100 text-green-700",
  webhook_test: "bg-blue-100 text-blue-700",
  bulk_import:  "bg-violet-100 text-violet-700",
  csv_import:   "bg-amber-100 text-amber-700",
  tpv_api:      "bg-purple-100 text-purple-700",
};

const SOURCE_LABELS = {
  webhook:      "webhook",
  webhook_test: "test",
  bulk_import:  "import",
  csv_import:   "csv",
  tpv_api:      "api",
};

function TicketRow({ ticket }) {
  const [open, setOpen] = useState(false);
  const date = ticket.created_at
    ? new Date(ticket.created_at).toLocaleString("es-ES", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";
  const total = (ticket.ticket_total || ticket.lines_total || 0).toFixed(2);
  const sourceStyle = SOURCE_STYLES[ticket.source] || "bg-muted text-muted-foreground";
  const sourceLabel = SOURCE_LABELS[ticket.source] || ticket.source || "—";

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`ticket-row-${ticket.ticket_number || ticket._id}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors text-left"
        data-testid={`ticket-toggle-${ticket.ticket_number || ticket._id}`}
      >
        <div className="flex-shrink-0 text-muted-foreground">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="w-24 flex-shrink-0">
          <p className="text-xs font-mono font-semibold text-foreground">
            #{ticket.ticket_number || "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">{ticket.lines_count} línea{ticket.lines_count !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{date}</p>
          {(ticket.table && ticket.table !== "0") && (
            <p className="text-[10px] text-muted-foreground/70">Mesa {ticket.table}</p>
          )}
        </div>
        {ticket.seller && (
          <div className="hidden sm:block w-20 flex-shrink-0 text-xs text-muted-foreground truncate">
            Vend. {ticket.seller}
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
          {PAYMENT_ICONS[ticket.payment_method] || <CreditCard className="w-3 h-3" />}
          <span className="hidden sm:inline">{ticket.payment_method || "—"}</span>
        </div>
        <Badge className={`text-[10px] px-1.5 py-0 border-0 flex-shrink-0 ${sourceStyle}`}>
          {sourceLabel}
        </Badge>
        <div className="w-20 text-right flex-shrink-0">
          <p className="text-sm font-bold">{total}€</p>
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-muted/20 divide-y divide-border/50">
          {(ticket.lines || []).map((line, i) => (
            <div key={i} className="flex items-center gap-3 px-10 py-2 text-xs">
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{line.product_name}</span>
              </div>
              <span className="text-muted-foreground w-12 text-right">×{line.quantity}</span>
              <span className="text-muted-foreground w-16 text-right">{(line.unit_price || 0).toFixed(2)}€/u</span>
              <span className="font-semibold w-16 text-right">{(line.total || 0).toFixed(2)}€</span>
            </div>
          ))}
          <div className="flex items-center justify-end gap-3 px-10 py-2 text-xs font-semibold text-muted-foreground">
            <span>{ticket.lines_count} artículo{ticket.lines_count !== 1 ? "s" : ""}</span>
            <span className="text-foreground font-bold text-sm">{total}€ total</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function TicketsPanel({ restaurantId, startDate, endDate }) {
  const [tickets, setTickets]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [loading, setLoading]       = useState(false);

  // Search state
  const [searchText, setSearchText] = useState("");      // ticket number search
  const [searchDate, setSearchDate] = useState("");      // specific date search
  const [activeSearch, setActiveSearch] = useState({ text: "", date: "" });

  const debounceRef = useRef(null);
  const PAGE_SIZE = 50;

  const isSearching = activeSearch.text !== "" || activeSearch.date !== "";

  const load = useCallback(async (p = 1, overrideSearch = null) => {
    if (!restaurantId) return;
    const s = overrideSearch ?? activeSearch;
    setLoading(true);
    try {
      const res = await api.getTickets(
        restaurantId,
        s.text ? null : startDate,   // ignore date range when searching by ticket number
        s.text ? null : endDate,
        p,
        PAGE_SIZE,
        s.text || null,
        s.date || null,
      );
      setTickets(res.tickets || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate, activeSearch]);

  // Reload when period or active search changes
  useEffect(() => { load(1); }, [load]);

  // Debounce ticket number input
  const handleSearchTextChange = (val) => {
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = { text: val, date: searchDate };
      setActiveSearch(next);
    }, 400);
  };

  const handleSearchDateChange = (val) => {
    setSearchDate(val);
    const next = { text: searchText, date: val };
    setActiveSearch(next);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchDate("");
    const next = { text: "", date: "" };
    setActiveSearch(next);
    load(1, next);
  };

  return (
    <Card data-testid="tickets-panel">
      <CardHeader className="pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" />
            Tickets del período
            {total > 0 && (
              <Badge variant="secondary" className="text-xs ml-1">{total.toLocaleString()}</Badge>
            )}
            {isSearching && (
              <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
                Búsqueda activa
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => load(page)} disabled={loading} data-testid="refresh-tickets-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        {/* Search bar */}
        <div className="flex flex-wrap gap-2" data-testid="tickets-search-bar">
          {/* Ticket number */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchText}
              onChange={e => handleSearchTextChange(e.target.value)}
              placeholder="Buscar nº ticket..."
              className="pl-8 h-8 text-xs"
              data-testid="ticket-search-input"
            />
            {searchText && (
              <button
                onClick={() => handleSearchTextChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Date filter */}
          <div className="relative">
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="date"
              value={searchDate}
              onChange={e => handleSearchDateChange(e.target.value)}
              className="h-8 pl-8 pr-2 rounded-md border border-input bg-background text-xs w-40"
              data-testid="ticket-date-filter"
            />
            {searchDate && (
              <button
                onClick={() => handleSearchDateChange("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {isSearching && (
            <Button variant="outline" size="sm" onClick={clearSearch} className="h-8 text-xs gap-1" data-testid="clear-search-btn">
              <X className="w-3 h-3" /> Limpiar
            </Button>
          )}
        </div>

        {isSearching ? (
          <p className="text-xs text-muted-foreground">
            {activeSearch.text && <span>Nº ticket: <span className="font-mono font-semibold text-foreground">"{activeSearch.text}"</span> · búsqueda global</span>}
            {activeSearch.text && activeSearch.date && " · "}
            {activeSearch.date && <span>Fecha: <span className="font-semibold text-foreground">{new Date(activeSearch.date).toLocaleDateString("es-ES")}</span></span>}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Haz clic en una fila para ver las líneas del ticket</p>
        )}
      </CardHeader>

      <CardContent className="space-y-2">
        {loading && tickets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            <Receipt className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>
              {isSearching
                ? "No se encontraron tickets para esta búsqueda"
                : "No hay tickets para el período seleccionado"}
            </p>
            {isSearching && (
              <Button variant="link" size="sm" className="mt-2 text-xs" onClick={clearSearch}>
                Limpiar búsqueda
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-3 pb-1 text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
              <div className="w-4 flex-shrink-0" />
              <div className="w-24 flex-shrink-0">Nº Ticket</div>
              <div className="flex-1">Fecha · Mesa</div>
              <div className="hidden sm:block w-20">Vendedor</div>
              <div className="w-16">Pago</div>
              <div className="w-14">Origen</div>
              <div className="w-20 text-right">Total</div>
            </div>

            {tickets.map(t => <TicketRow key={t._id || t.ticket_number} ticket={t} />)}

            {pages > 1 && (
              <div className="flex items-center justify-between pt-3">
                <span className="text-xs text-muted-foreground">
                  Página {page} de {pages} · {total} tickets
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="tickets-prev-page">
                    Anterior
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => load(page + 1)} data-testid="tickets-next-page">
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
